import { useEffect, useMemo, useState, useCallback } from 'react'
import { Link, Navigate } from 'react-router-dom'
import {ShieldCheck, Briefcase, GraduationCap, UserCheck, CheckCircle2, XCircle, Clock, RefreshCw, AlertTriangle, ExternalLink, FileText, HelpCircle, Home as HomeIcon, Building2, Search, Scale, Users, Map as MapIcon, Trash2, Image as ImageIcon, Pencil, } from 'lucide-react'
import toast from 'react-hot-toast'
import Layout from '@/components/Layout'
import Seo from '@/components/Seo'
import JobShareImageModal, { JobShareData } from '@/components/JobShareImageModal'
import JobFormModal from '@/components/JobFormModal'
import type { RecruiterJob, NewRecruiterJob } from '@/hooks/useRecruiterJobs'
import { lumi } from '@/lib/lumi'
import { useAuth } from '@/hooks/useAuth'
import {
  emailCandidate_DossierApproved,
  emailCandidate_DossierRejected,
  emailRecruiter_JobApproved,
  emailRecruiter_JobRejected,
} from '@/lib/emails'
import SeoEditor from '@/components/SeoEditor'
import FaqEditor from '@/components/FaqEditor'
import HomeEditor from '@/components/HomeEditor'
import LegalEditor from '@/components/LegalEditor'
import AvailableCandidatesAdmin from '@/components/AvailableCandidatesAdmin'
import SitemapEditor from '@/components/SitemapEditor'
import type { CompanyProfile, AccountType } from '@/hooks/useCompanyProfile'

// ===== Types =====
type ValidationStatus = 'pending' | 'approved' | 'rejected'

interface RecruiterJobDoc {
  _id: string
  title: string
  employer: string
  location: string
  department?: string
  category: 'BNSSA' | 'MNS'
  contractType: string
  description: string
  creator: string
  creatorEmail?: string
  creatorName?: string
  createdAt: string
  validationStatus?: ValidationStatus
  rejectionReason?: string
}

interface OrganismeFormationDoc {
  _id: string
  title: string
  organisme?: string
  category: string
  location?: string
  region?: string
  sessionDate?: string
  description?: string
  contact?: string
  creator: string
  creatorEmail?: string
  createdAt: string
  validationStatus?: ValidationStatus
  rejectionReason?: string
}

interface ProDossierDoc {
  _id: string
  category: 'BNSSA' | 'MNS'
  diplomeType?: string
  fullName?: string
  email?: string
  phone?: string
  companyName?: string
  raisonSociale?: string
  siret?: string
  companyPhone?: string
  telephoneEntreprise?: string
  companyAddress?: string
  adresseEntreprise?: string
  files?: Record<string, string>
  creator: string
  createdAt: string
  updatedAt: string
  validationStatus?: ValidationStatus
  rejectionReason?: string
}

type TabKey = 'jobs' | 'formations' | 'dossiers' | 'coordonnees' | 'inscriptions' | 'seo' | 'faq' | 'home' | 'legal' | 'cvtheque' | 'sitemap'

type RegistrationRole = 'candidate' | 'recruiter' | 'organisme'

interface RegistrationItem {
  creator: string
  email: string
  displayName: string
  role: RegistrationRole
  createdAt?: string
  updatedAt?: string
  companyId?: string
  applicationsCount: number
  availableCandidatesCount: number
  jobsCount: number
  formationsCount: number
  dossiersCount: number
}

// ===== Helpers =====
const STATUS_FILTERS: { value: ValidationStatus | 'all'; label: string }[] = [
  { value: 'pending', label: 'En attente' },
  { value: 'approved', label: 'Validés' },
  { value: 'rejected', label: 'Rejetés' },
  { value: 'all', label: 'Tous' },
]

function StatusBadge({ status }: { status: ValidationStatus | undefined }) {
  const s = status ?? 'pending'
  const map = {
    pending: { bg: 'bg-amber-100', text: 'text-amber-800', icon: Clock, label: 'En attente' },
    approved: { bg: 'bg-emerald-100', text: 'text-emerald-800', icon: CheckCircle2, label: 'Validé' },
    rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label: 'Rejeté' },
  }[s]
  const Icon = map.icon
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${map.bg} ${map.text}`}>
      <Icon className="w-3 h-3" />
      {map.label}
    </span>
  )
}

function formatDate(iso: string | undefined) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch {
    return iso
  }
}

// ===== Page =====
export default function AdminPanel() {
  const { user, isAuthenticated, isAdmin } = useAuth()
  const [tab, setTab] = useState<TabKey>('jobs')
  const [statusFilter, setStatusFilter] = useState<ValidationStatus | 'all'>('pending')
  const [loading, setLoading] = useState(false)

  const [jobs, setJobs] = useState<RecruiterJobDoc[]>([])
  const [formations, setFormations] = useState<OrganismeFormationDoc[]>([])
  const [dossiers, setDossiers] = useState<ProDossierDoc[]>([])
  const [companies, setCompanies] = useState<CompanyProfile[]>([])
  const [applicationsAll, setApplicationsAll] = useState<{ _id: string; creator?: string; creatorEmail?: string; fullName?: string; email?: string; createdAt?: string }[]>([])
  const [availableCandidatesAll, setAvailableCandidatesAll] = useState<{ _id: string; creator?: string; creatorEmail?: string; email?: string; createdAt?: string; updatedAt?: string }[]>([])
  const [companySearch, setCompanySearch] = useState('')
  const [companyTypeFilter, setCompanyTypeFilter] = useState<AccountType | 'all'>('all')
  const [registrationSearch, setRegistrationSearch] = useState('')
  const [registrationRoleFilter, setRegistrationRoleFilter] = useState<RegistrationRole | 'all'>('all')
  const [sharingJob, setSharingJob] = useState<JobShareData | null>(null)
  // Annonce en cours d'édition par l'administrateur (réutilise JobFormModal).
  const [editingJob, setEditingJob] = useState<RecruiterJob | null>(null)

  // ===== Fetchers =====
  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const [jobsRes, formationsRes, dossiersRes, companiesRes, applicationsRes, availableRes] = await Promise.all([
        lumi.entities.recruiter_jobs.list({ sort: { createdAt: -1 } }),
        lumi.entities.organisme_formations.list({ sort: { createdAt: -1 } }).catch(() => ({ list: [] })),
        lumi.entities.pro_dossiers.list({ sort: { updatedAt: -1 } }),
        lumi.entities.company_profiles
          .list({ sort: { updatedAt: -1 }, limit: 1000, skip: 0 })
          .catch((err) => {
            console.error('[admin] company_profiles.list failed', err)
            return { list: [] }
          }),
        lumi.entities.applications
          .list({ sort: { createdAt: -1 }, limit: 5000, skip: 0 })
          .catch((err) => {
            console.error('[admin] applications.list failed', err)
            return { list: [] }
          }),
        lumi.entities.available_candidates
          .list({ sort: { updatedAt: -1 }, limit: 5000, skip: 0 })
          .catch((err) => {
            console.error('[admin] available_candidates.list failed', err)
            return { list: [] }
          }),
      ])
      setJobs((jobsRes.list as RecruiterJobDoc[]) || [])
      setFormations((formationsRes.list as OrganismeFormationDoc[]) || [])
      setDossiers((dossiersRes.list as ProDossierDoc[]) || [])
      setCompanies((companiesRes.list as CompanyProfile[]) || [])
      setApplicationsAll((applicationsRes.list as { _id: string; creator?: string; creatorEmail?: string; fullName?: string; email?: string; createdAt?: string }[]) || [])
      setAvailableCandidatesAll((availableRes.list as { _id: string; creator?: string; creatorEmail?: string; email?: string; createdAt?: string; updatedAt?: string }[]) || [])
      console.log('admin.fetchAll OK', {
        jobs: jobsRes.list?.length,
        formations: formationsRes.list?.length,
        dossiers: dossiersRes.list?.length,
        companies: companiesRes.list?.length,
        applications: applicationsRes.list?.length,
        availableCandidates: availableRes.list?.length,
      })
    } catch (e) {
      console.error('admin.fetchAll failed:', e)
      toast.error('Impossible de charger les données admin.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isAdmin) {
      fetchAll()
    }
  }, [isAdmin, fetchAll])

  // ===== Actions =====
  const updateStatus = useCallback(
    async (
      collection: 'recruiter_jobs' | 'organisme_formations' | 'pro_dossiers',
      id: string,
      next: ValidationStatus,
      reason?: string,
    ) => {
      try {
        const now = new Date().toISOString()
        const patch: Record<string, unknown> = {
          validationStatus: next,
          validatedAt: now,
          updatedAt: now,
        }
        if (next === 'rejected' && reason) patch.rejectionReason = reason
        if (next === 'approved') patch.rejectionReason = ''

        await lumi.entities[collection].update(id, patch)
        console.log(`${collection}.update OK`, id, next)
        toast.success(
          next === 'approved' ? 'Validé avec succès.' : next === 'rejected' ? 'Rejeté.' : 'Mis à jour.',
        )

        // MAJ locale optimiste
        if (collection === 'recruiter_jobs') {
          setJobs((prev) =>
            prev.map((j) => (j._id === id ? { ...j, validationStatus: next, rejectionReason: reason ?? '' } : j)),
          )
        } else if (collection === 'organisme_formations') {
          setFormations((prev) =>
            prev.map((f) => (f._id === id ? { ...f, validationStatus: next, rejectionReason: reason ?? '' } : f)),
          )
        } else {
          setDossiers((prev) =>
            prev.map((d) => (d._id === id ? { ...d, validationStatus: next, rejectionReason: reason ?? '' } : d)),
          )
        }
      } catch (e) {
        console.error(`${collection}.update failed:`, e)
        toast.error('La mise à jour a échoué.')
      }
    },
    [],
  )

  const handleApprove = (
    collection: 'recruiter_jobs' | 'organisme_formations' | 'pro_dossiers',
    id: string,
  ) => {
    void updateStatus(collection, id, 'approved').then(() => {
      // Email 10 (dossier AE approuvé)
      if (collection == 'pro_dossiers') {
        const dossier = dossiers.find((d) => d._id === id)
        if (dossier) void sendDossierStatusEmail(dossier, 'approved')
      }
      // Email recruteur — annonce validée et publiée
      if (collection === 'recruiter_jobs') {
        const job = jobs.find((j) => j._id === id)
        if (job?.creatorEmail) {
          void emailRecruiter_JobApproved({
            recruiterEmail: job.creatorEmail,
            recruiterName: job.creatorName,
            jobTitle: job.title,
            jobEmployer: job.employer,
            category: job.category,
            contractType: job.contractType,
            jobId: job._id,
          }).catch((err) =>
            console.warn('[email] emailRecruiter_JobApproved failed (non bloquant):', err),
          )
        } else {
          console.warn('[email] Annonce validée mais aucun creatorEmail trouvé pour', id)
        }

        // Scan serveur des alertes emploi (TOUS les candidats) — best-effort.
        // Une offre qui vient d'être approuvée devient éligible aux alertes :
        // on déclenche immédiatement la Deno function `scanJobAlerts` afin que
        // chaque candidat concerné reçoive son email, sans dépendre du cron
        // (inactif en preview) ni d'une reconnexion du candidat.
        void lumi.functions
          .invoke('scanJobAlerts', { method: 'POST' })
          .then((res) => {
            console.log('[alerts] scanJobAlerts déclenché après validation', res)
          })
          .catch((err) =>
            console.warn('[alerts] scanJobAlerts failed (non bloquant):', err),
          )
      }
    })
  }

  // Envoi email selon transition de statut d'un dossier AE.
  // Email 9 (refus + motif) ou Email 10 (validation).
  const sendDossierStatusEmail = useCallback(
    async (dossier: ProDossierDoc, next: ValidationStatus, reason?: string) => {
      if (!dossier.email) return
      try {
        if (next === 'rejected') {
          await emailCandidate_DossierRejected({
            candidateEmail: dossier.email,
            candidateName: dossier.fullName,
            category: dossier.category,
            rejectionReason: reason,
          })
        } else if (next === 'approved') {
          await emailCandidate_DossierApproved({
            candidateEmail: dossier.email,
            candidateName: dossier.fullName,
            category: dossier.category,
          })
        }
      } catch (err) {
        console.warn('[email] Envoi statut dossier échoué (non bloquant) :', err)
      }
    },
    [],
  )

  const handleReject = (
    collection: 'recruiter_jobs' | 'organisme_formations' | 'pro_dossiers',
    id: string,
  ) => {
    const reason = window.prompt('Motif du rejet (optionnel) :') ?? ''
    void updateStatus(collection, id, 'rejected', reason).then(() => {
      // Email 9 (dossier AE refusé + motif)
      if (collection === 'pro_dossiers') {
        const dossier = dossiers.find((d) => d._id === id)
        if (dossier) void sendDossierStatusEmail(dossier, 'rejected', reason)
      }
      // Email recruteur — annonce refusée (avec motif)
      if (collection === 'recruiter_jobs') {
        const job = jobs.find((j) => j._id === id)
        if (job?.creatorEmail) {
          void emailRecruiter_JobRejected({
            recruiterEmail: job.creatorEmail,
            recruiterName: job.creatorName,
            jobTitle: job.title,
            jobEmployer: job.employer,
            category: job.category,
            contractType: job.contractType,
            rejectionReason: reason || undefined,
          }).catch((err) =>
            console.warn('[email] emailRecruiter_JobRejected failed (non bloquant):', err),
          )
        } else {
          console.warn('[email] Annonce refusée mais aucun creatorEmail trouvé pour', id)
        }
      }
    })
  }

  // Suppression définitive d'un élément rejeté (annonce, formation ou dossier).
  const handleDelete = useCallback(
    async (
      collection: 'recruiter_jobs' | 'organisme_formations' | 'pro_dossiers',
      id: string,
    ) => {
      const labelMap = {
        recruiter_jobs: 'cette annonce',
        organisme_formations: 'cette formation',
        pro_dossiers: 'ce dossier',
      } as const
      const label = labelMap[collection]
      const ok = window.confirm(
        `Supprimer définitivement ${label} ?\n\nCette action est irréversible.`,
      )
      if (!ok) return
      try {
        await lumi.entities[collection].delete(id)
        console.log(`${collection}.delete OK`, id)
        toast.success('Suppression effectuée.')
        if (collection === 'recruiter_jobs') {
          setJobs((prev) => prev.filter((j) => j._id !== id))
        } else if (collection === 'organisme_formations') {
          setFormations((prev) => prev.filter((f) => f._id !== id))
        } else {
          setDossiers((prev) => prev.filter((d) => d._id !== id))
        }
      } catch (e) {
        console.error(`${collection}.delete failed:`, e)
        toast.error('La suppression a échoué.')
      }
    },
    [],
  )

  // Édition admin du contenu d'une annonce recruteur (titre, lieu, contrat, dates…).
  // Réutilise JobFormModal et applique le même filtrage par type de contrat que
  // la création côté recruteur pour garder des données cohérentes.
  const handleSaveEditedJob = useCallback(
    async (data: NewRecruiterJob) => {
      if (!editingJob?._id) return
      const id = editingJob._id
      const isAEorInterim =
        data.contractType === 'auto-entrepreneur' || data.contractType === 'interim'
      const isFixedTerm =
        data.contractType === 'CDD' || data.contractType === 'temps-partiel-CDD'
      try {
        const now = new Date().toISOString()
        const patch = {
          title: data.title,
          employer: data.employer,
          location: data.location,
          department: data.department,
          region: data.region || '',
          category: data.category,
          contractType: data.contractType,
          salary: data.salary || '',
          hourlyRate: data.contractType === 'auto-entrepreneur' ? (data.hourlyRate || '') : '',
          description: data.description,
          requirements: data.requirements || [],
          benefits: data.benefits || [],
          deadline: data.deadline || '',
          // Date de début souhaitée — vidée pour AE/Intérim (mission ponctuelle à la place)
          startDate: isAEorInterim ? '' : (data.startDate || ''),
          // Date de fin — uniquement pour CDD & Temps Partiel CDD
          endDate: isFixedTerm ? (data.endDate || '') : '',
          urgent: data.urgent ?? false,
          logementPossible: data.logementPossible ?? false,
          horairesFixes: data.horairesFixes ?? false,
          coursParticuliers: data.category === 'MNS' ? (data.coursParticuliers ?? false) : false,
          possibiliteCDI: isFixedTerm ? (data.possibiliteCDI ?? false) : false,
          // Mission ponctuelle — uniquement pour AE et Intérim
          missionDate: isAEorInterim ? (data.missionDate || '') : '',
          missionStartTime: isAEorInterim ? (data.missionStartTime || '') : '',
          missionEndTime: isAEorInterim ? (data.missionEndTime || '') : '',
          missionHours: isAEorInterim && typeof data.missionHours === 'number' ? data.missionHours : undefined,
          missionPrice: data.contractType === 'interim' && typeof data.missionPrice === 'number' ? data.missionPrice : undefined,
          activities: isAEorInterim && Array.isArray(data.activities) ? data.activities : [],
          updatedAt: now,
        }
        await lumi.entities.recruiter_jobs.update(id, patch)
        console.log('admin recruiter_jobs.update OK', id)
        toast.success('Annonce mise à jour.')
        // MAJ locale optimiste
        setJobs((prev) =>
          prev.map((j) => (j._id === id ? { ...j, ...patch } as RecruiterJobDoc : j)),
        )
        setEditingJob(null)
      } catch (e) {
        console.error('admin recruiter_jobs.update failed:', e)
        toast.error("La mise à jour de l'annonce a échoué.")
      }
    },
    [editingJob],
  )

  // ===== Filtering =====
  const filteredJobs = useMemo(
    () =>
      jobs.filter((j) =>
        statusFilter === 'all' ? true : (j.validationStatus ?? 'pending') === statusFilter,
      ),
    [jobs, statusFilter],
  )
  const companiesByCreator = useMemo(() => {
    const map = new Map<string, CompanyProfile>()
    for (const c of companies) {
      if (c?.creator) map.set(c.creator, c)
    }
    return map
  }, [companies])

  // ===== Inscriptions (utilisateurs inscrits par rôle) =====
  const registrations = useMemo<RegistrationItem[]>(() => {
    const map = new Map<string, RegistrationItem>()

    // 1) Recruteurs & organismes via company_profiles
    for (const c of companies) {
      if (!c?.creator) continue
      const role: RegistrationRole = c.accountType === 'organisme' ? 'organisme' : 'recruiter'
      map.set(c.creator, {
        creator: c.creator,
        email: c.creatorEmail || '',
        displayName: c.raisonSociale || c.responsableNom || c.creatorEmail || c.creator,
        role,
        createdAt: c.createdAt,
        updatedAt: c.updatedAt,
        companyId: c._id,
        applicationsCount: 0,
        availableCandidatesCount: 0,
        jobsCount: 0,
        formationsCount: 0,
        dossiersCount: 0,
      })
    }

    // 2) Compteurs annonces / formations / dossiers (par creator)
    for (const j of jobs) {
      const r = j.creator ? map.get(j.creator) : undefined
      if (r) r.jobsCount += 1
    }
    for (const f of formations) {
      const r = f.creator ? map.get(f.creator) : undefined
      if (r) r.formationsCount += 1
    }
    for (const d of dossiers) {
      const r = d.creator ? map.get(d.creator) : undefined
      if (r) r.dossiersCount += 1
    }

    // 3) Candidats : agrégés depuis applications + available_candidates
    //    en excluant ceux déjà mappés comme recruteur/organisme
    const upsertCandidate = (
      creator: string | undefined,
      email: string | undefined,
      displayName: string | undefined,
      createdAt: string | undefined,
      kind: 'application' | 'available',
    ) => {
      if (!creator) return
      if (map.has(creator) && map.get(creator)!.role !== 'candidate') {
        // déjà recruteur/organisme : on incrémente quand même les compteurs candidats
        const existing = map.get(creator)!
        if (kind === 'application') existing.applicationsCount += 1
        else existing.availableCandidatesCount += 1
        return
      }
      const existing = map.get(creator)
      if (existing) {
        if (kind === 'application') existing.applicationsCount += 1
        else existing.availableCandidatesCount += 1
        if (!existing.email && email) existing.email = email
        if (!existing.displayName && displayName) existing.displayName = displayName
      } else {
        map.set(creator, {
          creator,
          email: email || '',
          displayName: displayName || email || creator,
          role: 'candidate',
          createdAt,
          updatedAt: createdAt,
          applicationsCount: kind === 'application' ? 1 : 0,
          availableCandidatesCount: kind === 'available' ? 1 : 0,
          jobsCount: 0,
          formationsCount: 0,
          dossiersCount: 0,
        })
      }
    }

    for (const a of applicationsAll) {
      upsertCandidate(a.creator, a.email || a.creatorEmail, a.fullName || a.email, a.createdAt, 'application')
    }
    for (const ac of availableCandidatesAll) {
      upsertCandidate(ac.creator, ac.email || ac.creatorEmail, ac.email, ac.createdAt || ac.updatedAt, 'available')
    }

    return Array.from(map.values()).sort((a, b) => {
      const da = a.createdAt || a.updatedAt || ''
      const db = b.createdAt || b.updatedAt || ''
      return db.localeCompare(da)
    })
  }, [companies, jobs, formations, dossiers, applicationsAll, availableCandidatesAll])

  const registrationCounts = useMemo(
    () => ({
      candidate: registrations.filter((r) => r.role === 'candidate').length,
      recruiter: registrations.filter((r) => r.role === 'recruiter').length,
      organisme: registrations.filter((r) => r.role === 'organisme').length,
    }),
    [registrations],
  )

  // Suppression complète d'une inscription (tous les documents liés au creator)
  const handleDeleteRegistration = useCallback(
    async (item: RegistrationItem) => {
      const ok = window.confirm(
        `Supprimer définitivement l'inscription de "${item.displayName}" (${item.email || item.creator}) ?\n\n` +
          `Toutes les données liées seront supprimées :\n` +
          `• ${item.applicationsCount} candidature(s)\n` +
          `• ${item.availableCandidatesCount} profil(s) CVthèque\n` +
          `• ${item.jobsCount} annonce(s)\n` +
          `• ${item.formationsCount} formation(s)\n` +
          `• ${item.dossiersCount} dossier(s) AE\n` +
          (item.companyId ? `• Profil entreprise/organisme\n` : '') +
          `\nCette action est irréversible.`,
      )
      if (!ok) return

      try {
        const tasks: Promise<unknown>[] = []

        // applications
        for (const a of applicationsAll.filter((x) => x.creator === item.creator)) {
          tasks.push(
            lumi.entities.applications.delete(a._id).catch((err) => {
              console.warn('[admin] applications.delete failed', a._id, err)
            }),
          )
        }
        // available_candidates
        for (const ac of availableCandidatesAll.filter((x) => x.creator === item.creator)) {
          tasks.push(
            lumi.entities.available_candidates.delete(ac._id).catch((err) => {
              console.warn('[admin] available_candidates.delete failed', ac._id, err)
            }),
          )
        }
        // recruiter_jobs
        for (const j of jobs.filter((x) => x.creator === item.creator)) {
          tasks.push(
            lumi.entities.recruiter_jobs.delete(j._id).catch((err) => {
              console.warn('[admin] recruiter_jobs.delete failed', j._id, err)
            }),
          )
        }
        // organisme_formations
        for (const f of formations.filter((x) => x.creator === item.creator)) {
          tasks.push(
            lumi.entities.organisme_formations.delete(f._id).catch((err) => {
              console.warn('[admin] organisme_formations.delete failed', f._id, err)
            }),
          )
        }
        // pro_dossiers
        for (const d of dossiers.filter((x) => x.creator === item.creator)) {
          tasks.push(
            lumi.entities.pro_dossiers.delete(d._id).catch((err) => {
              console.warn('[admin] pro_dossiers.delete failed', d._id, err)
            }),
          )
        }
        // company_profiles
        if (item.companyId) {
          tasks.push(
            lumi.entities.company_profiles.delete(item.companyId).catch((err) => {
              console.warn('[admin] company_profiles.delete failed', item.companyId, err)
            }),
          )
        }

        await Promise.all(tasks)
        console.log('admin.deleteRegistration OK', item.creator)
        toast.success('Inscription supprimée.')

        // MAJ locale optimiste
        setApplicationsAll((prev) => prev.filter((x) => x.creator !== item.creator))
        setAvailableCandidatesAll((prev) => prev.filter((x) => x.creator !== item.creator))
        setJobs((prev) => prev.filter((x) => x.creator !== item.creator))
        setFormations((prev) => prev.filter((x) => x.creator !== item.creator))
        setDossiers((prev) => prev.filter((x) => x.creator !== item.creator))
        setCompanies((prev) => prev.filter((x) => x.creator !== item.creator))
      } catch (e) {
        console.error('admin.deleteRegistration failed:', e)
        toast.error('La suppression a échoué.')
      }
    },
    [applicationsAll, availableCandidatesAll, jobs, formations, dossiers],
  )

  const filteredFormations = useMemo(
    () =>
      formations.filter((f) =>
        statusFilter === 'all' ? true : (f.validationStatus ?? 'pending') === statusFilter,
      ),
    [formations, statusFilter],
  )
  const filteredDossiers = useMemo(
    () =>
      dossiers.filter((d) =>
        statusFilter === 'all' ? true : (d.validationStatus ?? 'pending') === statusFilter,
      ),
    [dossiers, statusFilter],
  )

  const counts = useMemo(
    () => ({
      jobs: jobs.filter((j) => (j.validationStatus ?? 'pending') === 'pending').length,
      formations: formations.filter((f) => (f.validationStatus ?? 'pending') === 'pending').length,
      dossiers: dossiers.filter((d) => (d.validationStatus ?? 'pending') === 'pending').length,
    }),
    [jobs, formations, dossiers],
  )

  // ===== Guards =====
  if (!isAuthenticated) {
    return <Navigate to="/connexion" replace />
  }

  if (!isAdmin) {
    return (
      <Layout>
        <Seo title="Admin — Accès refusé" description="Espace réservé aux administrateurs." />
        <section className="min-h-[60vh] flex items-center justify-center px-4 py-20">
          <div className="max-w-md text-center bg-white border border-red-200 rounded-2xl p-8 shadow-sm">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Accès refusé</h1>
            <p className="text-sm text-gray-600 mb-5">
              Cet espace est réservé aux administrateurs de bnssa-mns.fr.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-800"
            >
              Retour à l'accueil
            </Link>
          </div>
        </section>
      </Layout>
    )
  }

  // ===== Render =====
  const tabs: { key: TabKey; label: string; icon: typeof Briefcase; count: number }[] = [
    { key: 'jobs', label: "Offres d'emploi", icon: Briefcase, count: counts.jobs },
    { key: 'formations', label: 'Formations organismes', icon: GraduationCap, count: counts.formations },
    { key: 'dossiers', label: 'Dossiers auto-entrepreneurs', icon: UserCheck, count: counts.dossiers },
    { key: 'coordonnees', label: 'Coordonnées entreprises', icon: Building2, count: 0 },
    { key: 'inscriptions', label: 'Inscriptions', icon: Users, count: registrations.length },
    { key: 'seo', label: 'Pages SEO', icon: FileText, count: 0 },
    { key: 'faq', label: 'FAQ', icon: HelpCircle, count: 0 },
    { key: 'home', label: "Page d'accueil", icon: HomeIcon, count: 0 },
    { key: 'legal', label: 'Pages légales', icon: Scale, count: 0 },
    { key: 'cvtheque', label: 'CVthèque', icon: Users, count: 0 },
    { key: 'sitemap', label: 'Sitemap', icon: MapIcon, count: 0 },
  ]

  return (
    <Layout>
      <Seo
        title="Espace administrateur — Emplois BNSSA & MNS"
        description="Validation admin des offres d'emploi, formations et dossiers auto-entrepreneurs sur bnssa-mns.fr."
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 via-slate-900 to-black text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center backdrop-blur">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <span className="text-xs font-bold uppercase tracking-widest text-gray-300">
              Espace administrateur
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">Validation des contenus</h1>
          <p className="text-gray-300 text-sm max-w-2xl">
            Connecté(e) en tant que <strong>{user?.email}</strong>. Validez ou rejetez les
            soumissions avant qu'elles ne soient publiées sur la plateforme.
          </p>
        </div>
      </section>

      {/* Tabs */}
      <section className="bg-white border-b border-gray-200 sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4 overflow-x-auto">
          <div className="flex items-center gap-1 py-2">
            {tabs.map((t) => {
              const Icon = t.icon
              const active = tab === t.key
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${
                    active
                      ? 'bg-gray-900 text-white'
                      : 'bg-transparent text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {t.label}
                  {t.count > 0 && (
                    <span
                      className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                        active ? 'bg-white text-gray-900' : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {t.count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
          <button
            onClick={fetchAll}
            disabled={loading}
            className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-md hover:bg-gray-100 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>
      </section>

      {/* Status filter */}
      {(tab === 'jobs' || tab === 'formations' || tab === 'dossiers') && (
        <section className="bg-gray-50 border-b border-gray-200 py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider mr-1">
            Statut :
          </span>
          {STATUS_FILTERS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setStatusFilter(opt.value)}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                statusFilter === opt.value
                  ? 'bg-gray-900 text-white'
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-100'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </section>
      )}

      {/* Content */}
      <section className="py-8 bg-gray-50 min-h-[50vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading && (
            <div className="text-center text-sm text-gray-500 py-8">
              <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
              Chargement…
            </div>
          )}

          {!loading && tab === 'jobs' && (
            <JobsTable
              items={filteredJobs}
              onApprove={(id) => handleApprove('recruiter_jobs', id)}
              onReject={(id) => handleReject('recruiter_jobs', id)}
              onDelete={(id) => handleDelete('recruiter_jobs', id)}
              onEdit={(job) => setEditingJob(job as unknown as RecruiterJob)}
              onShare={(job) =>
                setSharingJob({
                  title: job.title,
                  employer: job.employer,
                  location: job.location,
                  department: job.department,
                  category: job.category,
                  contractType: job.contractType,
                })
              }
            />
          )}
          {!loading && tab === 'formations' && (
            <FormationsTable items={filteredFormations} companiesByCreator={companiesByCreator} onApprove={(id) => handleApprove('organisme_formations', id)} onReject={(id) => handleReject('organisme_formations', id)} onDelete={(id) => handleDelete('organisme_formations', id)} />
          )}
          {!loading && tab === 'dossiers' && (
            <DossiersTable items={filteredDossiers} onApprove={(id) => handleApprove('pro_dossiers', id)} onReject={(id) => handleReject('pro_dossiers', id)} onDelete={(id) => handleDelete('pro_dossiers', id)} />
          )}
          {!loading && tab === 'coordonnees' && (
            <CompaniesTable
              items={companies}
              search={companySearch}
              onSearchChange={setCompanySearch}
              typeFilter={companyTypeFilter}
              onTypeFilterChange={setCompanyTypeFilter}
            />
          )}
          {!loading && tab === 'inscriptions' && (
            <RegistrationsTable
              items={registrations}
              search={registrationSearch}
              onSearchChange={setRegistrationSearch}
              roleFilter={registrationRoleFilter}
              onRoleFilterChange={setRegistrationRoleFilter}
              counts={registrationCounts}
              onDelete={handleDeleteRegistration}
            />
          )}
          {tab === 'seo' && (
            <SeoEditor />
          )}
          {tab === 'faq' && (
            <FaqEditor />
          )}
          {tab === 'home' && (
            <HomeEditor />
          )}
          {tab === 'legal' && (
            <LegalEditor />
          )}
          {tab === 'cvtheque' && (
            <AvailableCandidatesAdmin />
          )}
          {tab === 'sitemap' && (
            <SitemapEditor />
          )}
        </div>
      </section>

      {sharingJob && (
        <JobShareImageModal job={sharingJob} onClose={() => setSharingJob(null)} />
      )}

      {editingJob && (
        <JobFormModal
          existing={editingJob}
          onClose={() => setEditingJob(null)}
          onSave={handleSaveEditedJob}
        />
      )}
    </Layout>
  )
}

// ===== Sous-composants tableaux =====

function EmptyState({ label }: { label: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
      <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-3" />
      <p className="text-sm text-gray-600">{label}</p>
    </div>
  )
}

function ActionButtons({
  status,
  onApprove,
  onReject,
  onDelete,
}: {
  status: ValidationStatus | undefined
  onApprove: () => void
  onReject: () => void
  onDelete?: () => void
}) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        onClick={onApprove}
        disabled={status === 'approved'}
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <CheckCircle2 className="w-3.5 h-3.5" />
        Valider
      </button>
      <button
        onClick={onReject}
        disabled={status === 'rejected'}
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white border border-red-300 text-red-700 text-xs font-semibold hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <XCircle className="w-3.5 h-3.5" />
        Rejeter
      </button>
      {status === 'rejected' && onDelete && (
        <button
          onClick={onDelete}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-red-600 text-white text-xs font-semibold hover:bg-red-700"
          title="Supprimer définitivement"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Supprimer
        </button>
      )}
    </div>
  )
}

function JobsTable({
  items,
  onApprove,
  onReject,
  onDelete,
  onEdit,
  onShare,
}: {
  items: RecruiterJobDoc[]
  onApprove: (id: string) => void
  onReject: (id: string) => void
  onDelete: (id: string) => void
  onEdit: (job: RecruiterJobDoc) => void
  onShare: (job: RecruiterJobDoc) => void
}) {
  if (items.length === 0) return <EmptyState label="Aucune offre dans ce statut." />
  return (
    <div className="space-y-3">
      {items.map((j) => (
        <article key={j._id} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span
                  className={`inline-block px-2 py-0.5 text-[10px] font-bold rounded-md ${
                    j.category === 'BNSSA' ? 'bg-red-600 text-white' : 'bg-blue-600 text-white'
                  }`}
                >
                  {j.category}
                </span>
                <StatusBadge status={j.validationStatus} />
                <span className="text-[11px] text-gray-500">{formatDate(j.createdAt)}</span>
              </div>
              <h3 className="text-base font-bold text-gray-900 leading-snug mb-0.5">{j.title}</h3>
              <p className="text-sm text-gray-700">
                <strong>{j.employer}</strong> — {j.location} · {j.contractType}
              </p>
              <p className="mt-2 text-sm text-gray-600 line-clamp-2">{j.description}</p>
              {j.rejectionReason && (
                <p className="mt-2 text-xs text-red-700 bg-red-50 border border-red-100 rounded px-2 py-1">
                  Motif rejet : {j.rejectionReason}
                </p>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-3">
                <Link
                  to={`/offre/${j._id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-gray-600 hover:text-gray-900 inline-flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" />
                  Aperçu
                </Link>
                <button
                  onClick={() => onEdit(j)}
                  className="text-xs text-blue-700 hover:text-blue-900 inline-flex items-center gap-1 font-semibold"
                  title="Modifier l'annonce du recruteur"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Modifier
                </button>
                <button
                  onClick={() => onShare(j)}
                  className="text-xs text-fuchsia-700 hover:text-fuchsia-900 inline-flex items-center gap-1 font-semibold"
                  title="Générer un visuel de partage (LinkedIn / Facebook)"
                >
                  <ImageIcon className="w-3.5 h-3.5" />
                  Visuel
                </button>
              </div>
              <ActionButtons
                status={j.validationStatus}
                onApprove={() => onApprove(j._id)}
                onReject={() => onReject(j._id)}
                onDelete={() => onDelete(j._id)}
              />
            </div>
          </div>
        </article>
      ))}
    </div>
  )
}

function FormationsTable({
  items,
  companiesByCreator,
  onApprove,
  onReject,
  onDelete,
}: {
  items: OrganismeFormationDoc[]
  companiesByCreator?: Map<string, CompanyProfile>
  onApprove: (id: string) => void
  onReject: (id: string) => void
  onDelete: (id: string) => void
}) {
  if (items.length === 0) return <EmptyState label="Aucune formation dans ce statut." />
  return (
    <div className="space-y-3">
      {items.map((f) => (
        <article key={f._id} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="inline-block px-2 py-0.5 text-[10px] font-bold rounded-md bg-amber-600 text-white">
                  {f.category}
                </span>
                <StatusBadge status={f.validationStatus} />
                <span className="text-[11px] text-gray-500">
                  Session : {formatDate(f.sessionDate)}
                </span>
              </div>
              <h3 className="text-base font-bold text-gray-900 leading-snug mb-0.5">{f.title}</h3>
              <p className="text-sm text-gray-700">
                {f.organisme || f.creatorEmail || 'Organisme'} — {f.location || '—'} · {f.region || '—'}
              </p>
              {f.description && (
                <p className="mt-2 text-sm text-gray-600 line-clamp-2">{f.description}</p>
              )}
              {f.contact && (
                <p className="mt-1 text-xs text-gray-500">Contact : {f.contact}</p>
              )}
              {f.rejectionReason && (
                <p className="mt-2 text-xs text-red-700 bg-red-50 border border-red-100 rounded px-2 py-1">
                  Motif rejet : {f.rejectionReason}
                </p>
              )}
            </div>
            <ActionButtons
              status={f.validationStatus}
              onApprove={() => onApprove(f._id)}
              onReject={() => onReject(f._id)}
              onDelete={() => onDelete(f._id)}
            />
          </div>
        </article>
      ))}
    </div>
  )
}

function CompaniesTable({
  items,
  search,
  onSearchChange,
  typeFilter,
  onTypeFilterChange,
}: {
  items: CompanyProfile[]
  search: string
  onSearchChange: (v: string) => void
  typeFilter: AccountType | 'all'
  onTypeFilterChange: (v: AccountType | 'all') => void
}) {
  const filtered = items.filter((c) => {
    if (typeFilter !== 'all' && (c.accountType ?? 'recruiter') !== typeFilter) return false
    if (!search.trim()) return true
    const q = search.trim().toLowerCase()
    return (
      (c.raisonSociale || '').toLowerCase().includes(q) ||
      (c.siret || '').toLowerCase().includes(q) ||
      (c.responsableNom || '').toLowerCase().includes(q) ||
      (c.creatorEmail || '').toLowerCase().includes(q) ||
      (c.adresse || '').toLowerCase().includes(q) ||
      (c.telephone || '').toLowerCase().includes(q)
    )
  })

  const counts = {
    all: items.length,
    recruiter: items.filter((c) => (c.accountType ?? 'recruiter') === 'recruiter').length,
    organisme: items.filter((c) => c.accountType === 'organisme').length,
  }

  const TYPE_FILTERS: { value: AccountType | 'all'; label: string; count: number }[] = [
    { value: 'all', label: 'Tous', count: counts.all },
    { value: 'recruiter', label: 'Recruteurs', count: counts.recruiter },
    { value: 'organisme', label: 'Organismes', count: counts.organisme },
  ]

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Rechercher par raison sociale, SIRET, responsable, email…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {TYPE_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => onTypeFilterChange(f.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                typeFilter === f.value
                  ? 'bg-gray-900 text-white'
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-100'
              }`}
            >
              {f.label}
              <span
                className={`ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  typeFilter === f.value ? 'bg-white text-gray-900' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {f.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Privacy notice */}
      <div className="flex items-start gap-2 text-xs text-gray-600 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
        <ShieldCheck className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
        <p>
          Coordonnées internes des recruteurs et organismes — visibles uniquement dans les espaces privés. Jamais exposées dans les annonces publiques ni aux candidats.
        </p>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          label={
            items.length === 0
              ? "Aucune coordonnée d'entreprise enregistrée pour le moment."
              : 'Aucun résultat pour cette recherche.'
          }
        />
      ) : (
        <div className="space-y-3">
          {filtered.map((c) => {
            const isOrganisme = c.accountType === 'organisme'
            return (
              <article
                key={c._id}
                className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-md ${
                          isOrganisme ? 'bg-amber-600 text-white' : 'bg-gray-900 text-white'
                        }`}
                      >
                        <Building2 className="w-3 h-3" />
                        {isOrganisme ? 'Organisme' : 'Recruteur'}
                      </span>
                      <span className="text-[11px] text-gray-500">
                        MAJ : {formatDate(c.updatedAt)}
                      </span>
                    </div>
                    <h3 className="text-base font-bold text-gray-900 leading-snug mb-1">
                      {c.raisonSociale || '—'}
                    </h3>
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                      <div>
                        <dt className="text-[11px] uppercase font-semibold text-gray-500 tracking-wider">SIRET</dt>
                        <dd className="text-gray-800 font-mono">{c.siret || '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-[11px] uppercase font-semibold text-gray-500 tracking-wider">Téléphone</dt>
                        <dd className="text-gray-800">
                          {c.telephone ? (
                            <a href={`tel:${c.telephone}`} className="hover:underline">
                              {c.telephone}
                            </a>
                          ) : (
                            '—'
                          )}
                        </dd>
                      </div>
                      <div className="sm:col-span-2">
                        <dt className="text-[11px] uppercase font-semibold text-gray-500 tracking-wider">Adresse</dt>
                        <dd className="text-gray-800">{c.adresse || '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-[11px] uppercase font-semibold text-gray-500 tracking-wider">Responsable</dt>
                        <dd className="text-gray-800">
                          {c.responsableNom || '—'}
                          {c.responsableFonction ? (
                            <span className="text-gray-500"> · {c.responsableFonction}</span>
                          ) : null}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-[11px] uppercase font-semibold text-gray-500 tracking-wider">Email du compte</dt>
                        <dd className="text-gray-800">
                          {c.creatorEmail ? (
                            <a href={`mailto:${c.creatorEmail}`} className="hover:underline">
                              {c.creatorEmail}
                            </a>
                          ) : (
                            '—'
                          )}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </article>
            )
          })}
        </div>
      )}
    </div>
  )
}

function DossiersTable({
  items,
  onApprove,
  onReject,
  onDelete,
}: {
  items: ProDossierDoc[]
  onApprove: (id: string) => void
  onReject: (id: string) => void
  onDelete: (id: string) => void
}) {
  if (items.length === 0) return <EmptyState label="Aucun dossier dans ce statut." />
  return (
    <div className="space-y-3">
      {items.map((d) => {
        const fileCount = d.files ? Object.keys(d.files).length : 0
        return (
          <article key={d._id} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[260px]">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span
                    className={`inline-block px-2 py-0.5 text-[10px] font-bold rounded-md ${
                      d.category === 'BNSSA' ? 'bg-red-600 text-white' : 'bg-blue-600 text-white'
                    }`}
                  >
                    {d.category}
                  </span>
                  <StatusBadge status={d.validationStatus} />
                  <span className="text-[11px] text-gray-500">
                    MAJ : {formatDate(d.updatedAt)}
                  </span>
                </div>
                <h3 className="text-base font-bold text-gray-900 leading-snug mb-0.5">
                  {d.fullName || d.email || 'Candidat sans nom'}
                </h3>
                <p className="text-sm text-gray-700">
                  {d.diplomeType ? `Diplôme : ${d.diplomeType}` : 'Diplôme non précisé'}
                  {d.email && ` · ${d.email}`}
                  {d.phone && ` · ${d.phone}`}
                </p>

                {/* Informations entreprise (Auto-entrepreneur) */}
                {(d.companyName || d.raisonSociale || d.siret || d.companyPhone || d.telephoneEntreprise || d.companyAddress || d.adresseEntreprise) && (
                  <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Building2 className="w-3.5 h-3.5 text-gray-600" />
                      <span className="text-[11px] uppercase font-bold tracking-wider text-gray-600">
                        Informations entreprise
                      </span>
                    </div>
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
                      {(d.companyName || d.raisonSociale) && (
                        <div>
                          <dt className="text-[10px] uppercase font-semibold text-gray-500 tracking-wider">Raison sociale</dt>
                          <dd className="text-gray-800">{d.companyName || d.raisonSociale}</dd>
                        </div>
                      )}
                      {d.siret && (
                        <div>
                          <dt className="text-[10px] uppercase font-semibold text-gray-500 tracking-wider">SIRET</dt>
                          <dd className="text-gray-800 font-mono">{d.siret}</dd>
                        </div>
                      )}
                      {(d.companyPhone || d.telephoneEntreprise) && (
                        <div>
                          <dt className="text-[10px] uppercase font-semibold text-gray-500 tracking-wider">Téléphone pro</dt>
                          <dd className="text-gray-800">
                            <a href={`tel:${d.companyPhone || d.telephoneEntreprise}`} className="hover:underline">
                              {d.companyPhone || d.telephoneEntreprise}
                            </a>
                          </dd>
                        </div>
                      )}
                      {(d.companyAddress || d.adresseEntreprise) && (
                        <div className="sm:col-span-2">
                          <dt className="text-[10px] uppercase font-semibold text-gray-500 tracking-wider">Adresse du siège</dt>
                          <dd className="text-gray-800">{d.companyAddress || d.adresseEntreprise}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                )}

                <p className="mt-2 text-xs text-gray-500">
                  Pièces fournies : <strong>{fileCount}</strong>
                </p>
                {d.files && fileCount > 0 && (
                  <ul className="mt-2 flex flex-wrap gap-1.5">
                    {Object.entries(d.files).map(([key, value]) => {
                      const isUrl = typeof value === 'string' && /^https?:\/\//i.test(value)
                      return (
                        <li key={key}>
                          {isUrl ? (
                            <a
                              href={value}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-semibold hover:bg-emerald-100"
                              title={`Voir ${key}`}
                            >
                              <ExternalLink className="w-3 h-3" />
                              {key}
                            </a>
                          ) : (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 border border-gray-200 text-gray-600 text-[11px]"
                              title="Ancien format — fichier non disponible en ligne"
                            >
                              <Clock className="w-3 h-3" />
                              {key}
                            </span>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                )}
                {d.rejectionReason && (
                  <p className="mt-2 text-xs text-red-700 bg-red-50 border border-red-100 rounded px-2 py-1">
                    Motif rejet : {d.rejectionReason}
                  </p>
                )}
              </div>
              <ActionButtons
                status={d.validationStatus}
                onApprove={() => onApprove(d._id)}
                onReject={() => onReject(d._id)}
                onDelete={() => onDelete(d._id)}
              />
            </div>
          </article>
        )
      })}
    </div>
  )
}


// ===== RegistrationsTable =====
function RegistrationsTable({
  items,
  search,
  onSearchChange,
  roleFilter,
  onRoleFilterChange,
  counts,
  onDelete,
}: {
  items: RegistrationItem[]
  search: string
  onSearchChange: (v: string) => void
  roleFilter: RegistrationRole | 'all'
  onRoleFilterChange: (v: RegistrationRole | 'all') => void
  counts: { candidate: number; recruiter: number; organisme: number }
  onDelete: (item: RegistrationItem) => void
}) {
  const filtered = items.filter((r) => {
    if (roleFilter !== 'all' && r.role !== roleFilter) return false
    if (!search.trim()) return true
    const q = search.trim().toLowerCase()
    return (
      (r.displayName || '').toLowerCase().includes(q) ||
      (r.email || '').toLowerCase().includes(q) ||
      (r.creator || '').toLowerCase().includes(q)
    )
  })

  const ROLE_FILTERS: { value: RegistrationRole | 'all'; label: string; count: number }[] = [
    { value: 'all', label: 'Tous', count: items.length },
    { value: 'candidate', label: 'Candidats', count: counts.candidate },
    { value: 'recruiter', label: 'Recruteurs', count: counts.recruiter },
    { value: 'organisme', label: 'Organismes', count: counts.organisme },
  ]

  const roleBadge = (role: RegistrationRole) => {
    if (role === 'candidate')
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-md bg-emerald-600 text-white"><UserCheck className="w-3 h-3" />Candidat</span>
    if (role === 'recruiter')
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-md bg-gray-900 text-white"><Building2 className="w-3 h-3" />Recruteur</span>
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-md bg-amber-600 text-white"><Building2 className="w-3 h-3" />Organisme</span>
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Rechercher par nom, email…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {ROLE_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => onRoleFilterChange(f.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                roleFilter === f.value
                  ? 'bg-gray-900 text-white'
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-100'
              }`}
            >
              {f.label}
              <span
                className={`ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  roleFilter === f.value ? 'bg-white text-gray-900' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {f.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Privacy notice */}
      <div className="flex items-start gap-2 text-xs text-gray-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <p>
          La suppression d'une inscription efface définitivement toutes les données associées (candidatures, profil CVthèque, annonces, formations, dossiers AE, profil entreprise). Cette action est irréversible.
        </p>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          label={
            items.length === 0
              ? 'Aucune inscription enregistrée pour le moment.'
              : 'Aucun résultat pour cette recherche.'
          }
        />
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => (
            <article
              key={r.creator}
              className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {roleBadge(r.role)}
                    {(r.createdAt || r.updatedAt) && (
                      <span className="text-[11px] text-gray-500">
                        Inscrit le {formatDate(r.createdAt || r.updatedAt)}
                      </span>
                    )}
                  </div>
                  <h3 className="text-base font-bold text-gray-900 leading-snug mb-0.5 break-words">
                    {r.displayName || '—'}
                  </h3>
                  {r.email && (
                    <p className="text-sm text-gray-700 break-all">
                      <a href={`mailto:${r.email}`} className="hover:underline">
                        {r.email}
                      </a>
                    </p>
                  )}
                  <p className="mt-2 text-xs text-gray-500 flex flex-wrap items-center gap-x-3 gap-y-1">
                    {r.role === 'candidate' && (
                      <>
                        <span>
                          Candidatures : <strong className="text-gray-700">{r.applicationsCount}</strong>
                        </span>
                        <span>
                          CVthèque : <strong className="text-gray-700">{r.availableCandidatesCount}</strong>
                        </span>
                        <span>
                          Dossiers AE : <strong className="text-gray-700">{r.dossiersCount}</strong>
                        </span>
                      </>
                    )}
                    {r.role === 'recruiter' && (
                      <span>
                        Annonces publiées : <strong className="text-gray-700">{r.jobsCount}</strong>
                      </span>
                    )}
                    {r.role === 'organisme' && (
                      <span>
                        Formations : <strong className="text-gray-700">{r.formationsCount}</strong>
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => onDelete(r)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-red-600 text-white text-xs font-semibold hover:bg-red-700"
                    title="Supprimer définitivement cette inscription et toutes les données associées"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Supprimer
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  )
}
